import * as React from "react";
import { formSet, useCpFormValue } from "../../gvc/testNewForm/utils";
import { CardContent } from "../../../components/antd/card/CardContent";
import { Alert, Button, Card, Modal, notification } from "antd";
import { homeLink, request } from "../../../services/cpln";
import { parsedDomain } from "../../../services/utils";
import { store } from "../../../store";
import { CpFormItem } from "../../gvc/testNewForm/CpFormItem";
import { FormItemValue } from "../../../components/antd/form/FormItemValue";
import { FormLabel } from "../../../components/antd/form/FormLabel";
import { DomainDryRunConfig } from "./domainDryRunConfig";
import { useCpFormContext } from "../../../components/form/useCpFormContext";

interface Props extends React.PropsWithChildren {}
export const ApexConfig: React.FC<Props> = () => {
  const { id } = useCpFormContext();
  const name = useCpFormValue(`advanced.name`);
  const apex = useCpFormValue(`meta.dryRun.apex`);

  const [lastScrolledApex, setLastScrolledApex] = React.useState("");

  const ref = React.useRef<HTMLDivElement>(null as any);

  React.useEffect(() => {
    if (!apex) {
      return;
    }

    if (apex.apex === lastScrolledApex) {
      return;
    }
    ref.current.scrollIntoView();
    setLastScrolledApex(apex.apex);
  }, [apex]);

  const [isApexLoading, setIsApexLoading] = React.useState(false);
  const [showApexModal, setShowApexModal] = React.useState(false);
  const [apexResult, setApexResult] = React.useState<null | true | string>(null);
  const [isSuccessAlertClosed, setIsSuccessAlertClosed] = React.useState(false);

  const [dryRun, setDryRun] = React.useState<any>(null);

  async function fetchDryRun() {
    try {
      const res = await request({
        method: "post",
        url: homeLink("domain") + `?dryRun`,
        body: { name: parsedDomain(name).domain },
        dontThrowOnFailure: true,
      });
      console.log("apex dry run", res.status);
      console.log(res.data.details);
      setDryRun(res.data.details);
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      console.log("apex dry run error", errorMessage);
    }
  }

  async function createApex() {
    try {
      setIsApexLoading(true);
      await request({
        method: "post",
        url: homeLink("domain"),
        body: { name: parsedDomain(name).domain },
      });
      notification.success({ message: "Created Apex Domain" });
      setIsApexLoading(false);
      setShowApexModal(false);
      setApexResult(true);

      const form = store.getState().form.instances[id];
      const onBlurCounter = form.meta.onBlurCounter;
      formSet(id, `meta.onBlurCounter`, onBlurCounter + 1);
    } catch (e) {
      setIsApexLoading(false);
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;

      setApexResult(errorMessage);
    }
  }

  if (!apex && !isApexLoading && apexResult === null) {
    return null;
  }

  if (apexResult === true && !isSuccessAlertClosed) {
    return (
      <div ref={ref} className="cpln-form-section cpln-ant-card-container" data-section-id={`apex-config`}>
        <Card>
          <CardContent>
            <div className="w-fit">
              <Alert
                showIcon
                type={`success`}
                closable
                onClose={() => setIsSuccessAlertClosed(true)}
                message={`Created Apex`}
                description={`Apex domain was successfully created.`}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (apexResult === true && isSuccessAlertClosed) {
    return null;
  }

  if (typeof apexResult === "string" && !isSuccessAlertClosed) {
    return (
      <div ref={ref} className="cpln-form-section cpln-ant-card-container" data-section-id={`apex-config`}>
        <Card>
          <CardContent>
            <div className="w-fit">
              <Alert showIcon type={`error`} message={`Failed to Create Apex`} description={apexResult} />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div ref={ref} className="cpln-form-section cpln-ant-card-container" data-section-id={`apex-config`}>
        <Card>
          <CardContent>
            <div className="w-fit">
              <Alert
                showIcon
                type={`error`}
                className="w-fit"
                message={`Apex Must Exist - ${apex.apex}`}
                description={
                  <div className="flex flex-col gap-2 py-2">
                    <div>{apex.message}</div>
                  </div>
                }
              />
              <Button
                type={`primary`}
                className="mt-2"
                onClick={() => {
                  setShowApexModal(true);
                  fetchDryRun();
                }}
                block
              >
                Create Apex
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
      {showApexModal ? (
        <Modal
          open={showApexModal}
          title={"Create Apex Domain"}
          // width={dryRunApex.status !== 200 ? 1000 : undefined}
          onCancel={() => setShowApexModal(false)}
          onOk={createApex}
          cancelButtonProps={{ type: "default", danger: true, disabled: isApexLoading }}
          okButtonProps={{ type: "primary", loading: isApexLoading, disabled: isApexLoading }}
        >
          <CpFormItem>
            <FormLabel>Domain</FormLabel>
            <FormItemValue>{parsedDomain(name).domain}</FormItemValue>
          </CpFormItem>
          <div className="flex flex-col gap-8">
            {dryRun?.ownershipConfig && dryRun.ownershipConfig.length > 0 ? (
              <div>
                <DomainDryRunConfig label={`Ownership Config`} configs={dryRun.ownershipConfig} />
              </div>
            ) : null}
            {dryRun?.dnsConfig && dryRun.dnsConfig.length > 0 ? (
              <div>
                <DomainDryRunConfig label={`DNS Config`} configs={dryRun.dnsConfig} />
              </div>
            ) : null}
          </div>
        </Modal>
      ) : null}
    </>
  );
};
